import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { toast } from "react-toastify";
import { Home, UserPlus, X } from "react-feather";
import { userService } from "../../../services/userService";
import { roomService } from "../../../services/roomService";
import ConfirmDeleteDialog from "../../Common/ConfirmDeleteDialog";

/**
 * AssignUserToRoomModal - Modal for managing user assignments to a room.
 * Manages its own state for users, assigned users, search, and tabs.
 * Props:
 *   isOpen: bool
 *   room: the room object (assigningUserToRoom)
 *   onClose: () => void
 *   siteId: number/string (not used directly but kept for potential refresh)
 *   onSuccess: () => void — called after a successful assign/unassign to refresh parent if needed
 */
const AssignUserToRoomModal = ({ isOpen, room, onClose, siteId, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("assign");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loadingAssignedUsers, setLoadingAssignedUsers] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [assigningUser, setAssigningUser] = useState(false);
  const [unassigningUser, setUnassigningUser] = useState(null);
  const [unassigningUserLoading, setUnassigningUserLoading] = useState(false);

  useEffect(() => {
    if (isOpen && room) {
      setActiveTab("assign");
      setSelectedUserName("");
      setUserSearchTerm("");
      fetchUsers();
      fetchAssignedUsers(room.id);
    }
  }, [isOpen, room]);

  const fetchUsers = async (searchName = "") => {
    setLoadingUsers(true);
    try {
      const params = {
        userType: "USER",
        ...(searchName?.trim() && { name: searchName.trim() }),
      };
      const response = await userService.list(params);
      setUsers(response.content || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAssignedUsers = async (roomId) => {
    setLoadingAssignedUsers(true);
    try {
      const response = await roomService.getUsers(roomId);
      setAssignedUsers(response || []);
    } catch (error) {
      console.error("Error fetching assigned users:", error);
      toast.error("Failed to load assigned users");
      setAssignedUsers([]);
    } finally {
      setLoadingAssignedUsers(false);
    }
  };

  const handleUserSearch = (searchTerm) => {
    setUserSearchTerm(searchTerm);
    fetchUsers(searchTerm);
  };

  const handleConfirmAssignUser = async () => {
    if (!selectedUserName) {
      toast.error("Please select a user");
      return;
    }

    setAssigningUser(true);
    try {
      const assignData = {
        userName: selectedUserName,
        roomId: room.id,
      };

      await roomService.assignUser(assignData);
      toast.success("User assigned to room successfully");

      await fetchAssignedUsers(room.id);

      setSelectedUserName("");
      setUserSearchTerm("");
      setActiveTab("assigned");

      fetchUsers();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error assigning user to room:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to assign user to room");
      }
    } finally {
      setAssigningUser(false);
    }
  };

  const handleUnassignUser = (assignedUser) => {
    setUnassigningUser(assignedUser);
  };

  const handleCancelUnassignUser = () => {
    setUnassigningUser(null);
  };

  const handleConfirmUnassignUser = async () => {
    if (!unassigningUser) return;

    setUnassigningUserLoading(true);
    try {
      await roomService.unassignUser({ userName: unassigningUser.userName, roomId: room.id });
      toast.success("User unassigned from room successfully");

      await fetchAssignedUsers(room.id);
      fetchUsers(userSearchTerm);

      setUnassigningUser(null);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error unassigning user from room:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to unassign user from room");
      }
    } finally {
      setUnassigningUserLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} size="md">
        <ModalHeader toggle={onClose}>Manage Room Users</ModalHeader>
        <ModalBody>
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex align-items-center">
              <Home size={20} className="me-2 text-warning" />
              <div>
                <small className="text-muted">Managing users for room:</small>
                <div className="fw-bold">{room?.name}</div>
                <small className="text-muted">Room ID: {room?.id}</small>
              </div>
            </div>
          </div>

          {/* Tabs for Assign and Assigned Users */}
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === "assign" ? "active" : ""}
                onClick={() => setActiveTab("assign")}
                style={{ cursor: "pointer" }}
              >
                Assign User
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "assigned" ? "active" : ""}
                onClick={() => setActiveTab("assigned")}
                style={{ cursor: "pointer" }}
              >
                Assigned Users ({assignedUsers.length})
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-3">
            {/* Assign User Tab */}
            <TabPane tabId="assign">
              <FormGroup>
                <Label for="userSearch">Search Users</Label>
                <Input
                  id="userSearch"
                  type="text"
                  placeholder="Search by name..."
                  value={userSearchTerm}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  disabled={assigningUser}
                />
                <small className="text-muted">Type to search for users by name</small>
              </FormGroup>

              <FormGroup>
                <Label for="userSelect">
                  Select User <span className="text-danger">*</span>
                </Label>
                <Input
                  id="userSelect"
                  type="select"
                  value={selectedUserName}
                  onChange={(e) => setSelectedUserName(e.target.value)}
                  disabled={assigningUser || loadingUsers}
                  size="8"
                >
                  <option value="">Select a user...</option>
                  {users
                    .filter(
                      (user) =>
                        !assignedUsers.some((assigned) => assigned.userName === user.userName)
                    )
                    .map((user) => (
                      <option key={user.id} value={user.userName}>
                        {user.userName} - {user.email}
                        {!user.active && " (Inactive)"}
                        {!user.verified && " (Unverified)"}
                      </option>
                    ))}
                </Input>
                {loadingUsers && (
                  <div className="mt-2">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    <small className="text-muted">Loading users...</small>
                  </div>
                )}
                {!loadingUsers &&
                  users.filter(
                    (user) =>
                      !assignedUsers.some((assigned) => assigned.userName === user.userName)
                  ).length === 0 &&
                  userSearchTerm && (
                    <small className="text-muted">
                      No unassigned users found matching "{userSearchTerm}"
                    </small>
                  )}
              </FormGroup>

              <div className="alert alert-info" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
                <strong>Note:</strong> The selected user will be assigned to room "{room?.name}"
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button color="secondary" onClick={onClose} disabled={assigningUser}>
                  Cancel
                </Button>
                <Button
                  color="warning"
                  onClick={handleConfirmAssignUser}
                  disabled={assigningUser || loadingUsers || !selectedUserName}
                >
                  {assigningUser ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus size={14} className="me-1" />
                      Assign User
                    </>
                  )}
                </Button>
              </div>
            </TabPane>

            {/* Assigned Users Tab */}
            <TabPane tabId="assigned">
              {loadingAssignedUsers ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2 text-muted">Loading assigned users...</div>
                </div>
              ) : assignedUsers.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <UserPlus size={48} style={{ opacity: 0.3 }} className="mb-3" />
                  <p className="mb-0">No users assigned to this room yet.</p>
                  <p className="small">Click on "Assign User" tab to assign users to this room.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ fontSize: "0.85rem" }}>Username</th>
                        <th style={{ fontSize: "0.85rem" }}>Email</th>
                        <th style={{ fontSize: "0.85rem" }}>Assigned By</th>
                        <th style={{ fontSize: "0.85rem" }}>Assigned At</th>
                        <th style={{ fontSize: "0.85rem", textAlign: "center" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedUsers.map((assignedUser, index) => (
                        <tr key={index}>
                          <td style={{ fontSize: "0.85rem", fontWeight: "500" }}>
                            {assignedUser.userName || "-"}
                          </td>
                          <td style={{ fontSize: "0.85rem" }}>{assignedUser.email || "-"}</td>
                          <td style={{ fontSize: "0.85rem" }}>{assignedUser.assignedBy || "-"}</td>
                          <td style={{ fontSize: "0.85rem" }}>
                            {assignedUser.assignedAt
                              ? new Date(assignedUser.assignedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              onClick={() => handleUnassignUser(assignedUser)}
                              title="Unassign user from room"
                              style={{ padding: "2px 8px", fontSize: "0.75rem" }}
                            >
                              <X size={12} className="me-1" />
                              Unassign
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Unassign User from Room Confirmation */}
      <ConfirmDeleteDialog
        isOpen={!!unassigningUser}
        headerText="Confirm Unassign"
        title="Unassign User"
        subtitle="This action will remove user access"
        icon={X}
        bodyText={
          <>
            Are you sure you want to unassign user{" "}
            <strong>"{unassigningUser?.userName}"</strong> from room{" "}
            <strong>"{room?.name}"</strong>?
          </>
        }
        warningText="The user will no longer have access to this room."
        warningLabel="Note"
        actionText="Unassign User"
        loadingText="Unassigning..."
        onClose={handleCancelUnassignUser}
        onConfirm={handleConfirmUnassignUser}
        loading={unassigningUserLoading}
      />
    </>
  );
};

export default AssignUserToRoomModal;
